import { useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckSquare, CheckCircle2, AlertTriangle, Clock, ArrowRight, Info, Users,
  FileText, Eye, Upload, XCircle
} from "lucide-react";

const ownerOptions = [
  "Builder team", "External planner", "Draftsperson / architect", "Permit coordinator", "Owner / agent"
];

const checks = [
  { id: "ck1", title: "Planning permit application form", category: "Completeness", status: "pass", note: "All fields completed, signatures present" },
  { id: "ck2", title: "Title and survey plan", category: "Completeness", status: "pass", note: "Current version, matches lot address" },
  { id: "ck3", title: "Architectural drawings v3", category: "Version", status: "warning", note: "Version mismatch: drawings say v2 but latest is v3" },
  { id: "ck4", title: "Shadow diagrams", category: "Missing", status: "fail", note: "Shadow diagrams not included in pack" },
  { id: "ck5", title: "Traffic response", category: "Missing", status: "fail", note: "Traffic response from consultant not received" },
  { id: "ck6", title: "Town planning report", category: "Naming", status: "warning", note: "File named 'TP_Report_FINAL_v2(1).pdf' - recommend consistent naming" },
  { id: "ck7", title: "Site context photos", category: "Completeness", status: "pass", note: "4 photos included, clear quality" },
  { id: "ck8", title: "Engineering preliminary assessment", category: "Signatures", status: "warning", note: "Date field blank on page 3" },
  { id: "ck9", title: "Stormwater management plan", category: "Missing", status: "fail", note: "Not included. Required by council." },
  { id: "ck10", title: "Neighbourhood character statement", category: "Completeness", status: "pass", note: "Present and complete" },
];

const statusIcons = {
  "pass": { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-50", label: "Ready" },
  "warning": { icon: AlertTriangle, color: "text-amber-500", bg: "bg-amber-50", label: "Warning" },
  "fail": { icon: XCircle, color: "text-red-500", bg: "bg-red-50", label: "Critical" },
};

export default function PreSubmissionCheck() {
  const [selectedOwner, setSelectedOwner] = useState("Builder team");

  const critical = checks.filter(c => c.status === "fail").length;
  const warnings = checks.filter(c => c.status === "warning").length;
  const ready = checks.filter(c => c.status === "pass").length;

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="pre-submission-check-page">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Review a lodgement pack or consultant handover before it moves into the next workflow. Checks completeness, versions, and missing items.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Fix critical issues, review warnings, then the pack is ready for lodgement or handover to the builder workflow.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Builder PM, permit admin, planner. Supporting review helper only. Does not guarantee approval.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Owner selector */}
        <Card className="border-slate-200">
          <CardContent className="py-4 px-5">
            <p className="text-xs font-semibold text-slate-600 mb-3">Who owns lodgement?</p>
            <div className="flex flex-wrap gap-2">
              {ownerOptions.map(o => (
                <Button
                  key={o}
                  size="sm"
                  variant={selectedOwner === o ? "default" : "outline"}
                  className={`text-xs ${selectedOwner === o ? "bg-slate-900 text-white" : ""}`}
                  onClick={() => setSelectedOwner(o)}
                  data-testid={`owner-${o.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {o}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-red-100 bg-red-50">
            <CardContent className="py-3 px-4 text-center">
              <p className="text-xs font-medium text-slate-500">Critical issues</p>
              <p className="text-2xl font-heading font-bold text-red-600">{critical}</p>
            </CardContent>
          </Card>
          <Card className="border-amber-100 bg-amber-50">
            <CardContent className="py-3 px-4 text-center">
              <p className="text-xs font-medium text-slate-500">Warnings</p>
              <p className="text-2xl font-heading font-bold text-amber-600">{warnings}</p>
            </CardContent>
          </Card>
          <Card className={critical > 0 ? "border-red-100 bg-red-50" : "border-emerald-100 bg-emerald-50"}>
            <CardContent className="py-3 px-4 text-center">
              <p className="text-xs font-medium text-slate-500">Pack status</p>
              <p className={`text-sm font-heading font-bold ${critical > 0 ? "text-red-600" : "text-emerald-600"}`}>
                {critical > 0 ? "Not ready" : "Ready for lodgement"}
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="all">
          <TabsList className="bg-slate-100 p-1 h-auto">
            <TabsTrigger value="all" className="text-xs">All checks ({checks.length})</TabsTrigger>
            <TabsTrigger value="before" className="text-xs">Before consultant lodges</TabsTrigger>
            <TabsTrigger value="handover" className="text-xs">Consultant handover</TabsTrigger>
            <TabsTrigger value="audit" className="text-xs">Builder-side audit</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-4">
            <Card className="border-slate-200" data-testid="checks-list">
              <CardContent className="py-4 space-y-2">
                {checks.map(check => {
                  const s = statusIcons[check.status];
                  const Icon = s.icon;
                  return (
                    <div key={check.id} className={`flex items-center justify-between py-3 px-4 ${s.bg} border border-slate-200 rounded-lg`} data-testid={`check-${check.id}`}>
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 ${s.color}`} />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{check.title}</p>
                          <p className="text-xs text-slate-500">{check.note}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[9px]">{check.category}</Badge>
                        <Badge className={`${s.bg} ${s.color.replace("text-", "text-")} border-slate-200 text-[10px]`}>{s.label}</Badge>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          {["before", "handover", "audit"].map(tab => (
            <TabsContent key={tab} value={tab} className="mt-4">
              <Card className="border-slate-200">
                <CardContent className="py-8 text-center">
                  <p className="text-sm text-slate-400">
                    {tab === "before" ? "Pre-lodgement checks will appear here when a consultant is submitting." :
                     tab === "handover" ? "Consultant handover review items appear here after the consultant delivers." :
                     "Builder-side audit trail for the pack review process."}
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        <Card className="border-slate-200 bg-slate-50/30">
          <CardContent className="py-3 px-4 flex items-start gap-2">
            <Info className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-500">This is a supporting review helper only. It does not claim perfect council knowledge, imply guaranteed approval, replace planner judgement, auto-lodge, or imply legal certainty.</p>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
